import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminDisputesApi,
  adminDisputesKeys,
} from '@/features/admin-finance/api/admin-disputes.api';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { bookingStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { financeListSx, financeRowSx } from '@/features/admin-finance/styles/financeSurfaces';
import { formatBookingWhen } from '@/shared/utils/bookingWhen';

export function FinanceDisputesPanel() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<'guide_favor' | 'traveler_favor'>('guide_favor');
  const [adminNotes, setAdminNotes] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: adminDisputesKeys.list({ page: 1, limit: 50 }),
    queryFn: () => adminDisputesApi.list({ page: 1, limit: 50 }),
  });

  const resolve = useMutation({
    mutationFn: () =>
      adminDisputesApi.resolve(selectedId!, { resolution, adminNotes: adminNotes || undefined }),
    onSuccess: () => {
      setSelectedId(null);
      setAdminNotes('');
      void queryClient.invalidateQueries({ queryKey: adminDisputesKeys.all });
    },
  });

  const disputes = data?.data ?? [];
  const selected = disputes.find((d) => d.id === selectedId);

  return (
    <Box>
      <Box sx={financeListSx}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ borderRadius: 0, m: 2 }}>
            Failed to load disputes.
          </Alert>
        )}
        {!isLoading && disputes.length === 0 && (
          <Alert severity="success" sx={{ borderRadius: 0, m: 2 }}>
            No open disputes.
          </Alert>
        )}

        {disputes.map((booking) => (
          <Box key={booking.id} sx={financeRowSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography fontWeight={700}>
                  Booking #{booking.id.slice(-8).toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {formatBookingWhen(booking)} ·{' '}
                  {formatGuidePrice(booking.grossAmount, booking.currency)}
                </Typography>
                {booking.disputeReason && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Reason:</strong> {booking.disputeReason}
                  </Typography>
                )}
              </Box>
              <StatusBadge
                status={bookingStatusToBadge(booking.status)}
                label={formatStatusLabel(booking.status)}
                sx={{ borderRadius: 0 }}
              />
            </Box>
            <Button size="small" sx={{ mt: 1.25, borderRadius: 0, px: 0 }} onClick={() => setSelectedId(booking.id)}>
              Resolve
            </Button>
          </Box>
        ))}
      </Box>

      {selected && (
        <Box
          sx={{
            mt: 2,
            p: 2.5,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Resolve dispute
          </Typography>
          <TextField
            select
            fullWidth
            label="Resolution"
            value={resolution}
            onChange={(e) => setResolution(e.target.value as 'guide_favor' | 'traveler_favor')}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          >
            <MenuItem value="guide_favor">Guide favor — complete trip, release earnings</MenuItem>
            <MenuItem value="traveler_favor">
              Traveler favor — refund payment and reverse settlement
            </MenuItem>
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Admin notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={{ borderRadius: 0 }} onClick={() => setSelectedId(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: 0 }}
              disabled={resolve.isPending}
              onClick={() => resolve.mutate()}
            >
              Confirm resolution
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
